import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "@client/lib/api";

/**
 * One client for the whole app. `staleTime: 30s` is the figure in
 * docs/08-frontend.md#state-management: long enough that moving between the
 * product list and a product does not refetch, short enough that a colleague's
 * new scenario shows up without a reload.
 *
 * The retry rule matters more than the number. A 403 or a 404 is an answer, not
 * a hiccup — retrying it three times delays the error message by a second and
 * changes nothing — so only 5xx and genuine network failures are retried.
 */
export function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 && error.status !== 501;
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => failureCount < 2 && isRetryable(error),
        refetchOnWindowFocus: true,
      },
      mutations: {
        // A mutation that failed halfway is not safe to repeat on its own
        // judgement; the user presses the button again if they want that.
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

/**
 * The query key vocabulary, in one place, so an invalidation after a mutation
 * cannot miss a list because someone spelled its key differently.
 *
 * `products.list` takes the filters: two filtered lists are two cached results,
 * and `invalidate(["products"])` still refreshes every one of them.
 */
export const queryKeys = {
  products: {
    all: ["products"] as const,
    list: (filters: object) => ["products", "list", filters] as const,
    detail: (idOrSlug: string) => ["products", "detail", idOrSlug] as const,
  },
  scenarios: {
    all: ["scenarios"] as const,
    list: (productId: string, filters: object) =>
      ["scenarios", "list", productId, filters] as const,
    detail: (id: string) => ["scenarios", "detail", id] as const,
    versions: (id: string) => ["scenarios", "versions", id] as const,
    version: (id: string, version: number) => ["scenarios", "versions", id, version] as const,
    // Sorted, so /compare?ids=a,b and ?ids=b,a share one cached answer. The
    // screen keeps the caller's order for its columns; the cache does not care.
    compare: (ids: readonly string[]) => ["scenarios", "compare", [...ids].sort()] as const,
  },
  admin: {
    all: ["admin"] as const,
    allowList: (filters: object) => ["admin", "allowlist", filters] as const,
    users: (filters: object) => ["admin", "users", filters] as const,
    audit: (filters: object) => ["admin", "audit", filters] as const,
  },
} as const;
